import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { array, bool, object, shape, string } from 'prop-types';
import { PrismicRichText } from '@prismicio/react';
import {
  Footer,
  LayoutSingleColumn,
  LayoutWrapperFooter,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  Page,
} from '../../components';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import PrismicSectionHero from '../../components/PrismicSectionHero/PrismicSectionHero';
import CaseStudyCard from '../../components/CaseStudyCard/CaseStudyCard';
import { formatCaseStudyPrismicData } from '../../util/data';
import { propTypes } from '../../util/types';

import css from './CaseStudyPage.module.css';

const formatDateOptions = { year: 'numeric', month: 'long', day: 'numeric' };

const CaseStudyPageComponent = props => {
  const {
    params: rawParams,
    intl,
    caseStudy,
    scrollingDisabled,
    relatedCaseStudies = [],
    fetchCaseStudyError,
    fetchCaseStudyInProgress,
  } = props;

  const { slug } = rawParams;
  const {
    postDate,
    writer,
    title,
    image,
    content,
    metaTitle,
    metaDescription,
    alt
  } = formatCaseStudyPrismicData(caseStudy);
  const schemaTitle =
    metaTitle || title || intl.formatMessage({ id: 'CaseStudyPage.schemaTitle' });
  const hasRelatedPosts = relatedCaseStudies.length > 0;

  return (
    <Page
      title={schemaTitle}
      description={metaDescription}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: metaDescription,
        name: schemaTitle,
        image: image,
      }}
    >
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer hideSearchbar />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain>
          <PrismicSectionHero
            backgroundImage={image}
            intl={intl}
            linkParams={{ slug }}
            className={css.prismicSectionHero}
            alt={alt}
            disableLink
          />
          <div className={css.wrapper}>
            {fetchCaseStudyError && (
              <p className={css.error}>
                <FormattedMessage id="CaseStudyPage.errorCaseStudy" />
              </p>
            )}
            {fetchCaseStudyInProgress && (
              <p>
                <FormattedMessage id="CaseStudyPage.loadingCaseStudy" />
              </p>
            )}
            <h1 className={css.title}>{title}</h1>
            <p className={css.metaInfo}>
              {postDate?.toLocaleDateString('en-US', formatDateOptions)}
            </p>
            <p className={css.metaInfo}>
              {intl.formatMessage({ id: 'CaseStudyPage.postedBy' }, { writer })}
            </p>
            <div className={css.contentWrapper}>
              <PrismicRichText field={content} />
            </div>
            {hasRelatedPosts && (
              <>
                <div className={css.heading}>
                  {intl.formatMessage({ id: 'CaseStudyPage.relatedPosts' })}
                </div>
                <div className={css.caseStudies}>
                  {relatedCaseStudies.map(caseStudy => (
                    <CaseStudyCard
                      key={caseStudy.id}
                      caseStudy={caseStudy}
                      className={css.caseStudy}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </Page>
  );
};

CaseStudyPageComponent.defaultProps = {
  params: null,
  caseStudy: null,
  relatedCaseStudies: [],
  fetchCaseStudyError: null,
};

CaseStudyPageComponent.propTypes = {
  params: shape({
    slug: string,
  }),
  caseStudy: object,
  scrollingDisabled: bool.isRequired,
  relatedCaseStudies: array,
  fetchCaseStudyError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const {
    fetchCaseStudyInProgress,
    fetchCaseStudyError,
    caseStudy,
    relatedCaseStudies,
  } = state.CaseStudyPage;

  return {
    fetchCaseStudyInProgress,
    fetchCaseStudyError,
    caseStudy,
    scrollingDisabled: isScrollingDisabled(state),
    relatedCaseStudies,
  };
};

const CaseStudyPage = compose(
  withRouter,
  connect(mapStateToProps),
  injectIntl
)(CaseStudyPageComponent);

export default CaseStudyPage;
